import moment from "moment";
import {
  getCompanyNowDate,
  getCompanyNowParts,
  parseUtcOffsetMinutes,
} from "../../utils/companyDateTime";

/** Preset ids for report period (rolling ranges recomputed on each fetch). */
export const DATE_PRESET = {
  LAST_1: "last1",
  LAST_2: "last2",
  LAST_5: "last5",
  THIS_WEEK: "thisWeek",
  LAST_7: "last7",
  LAST_30: "last30",
  START_TO_NOW: "startToNow",
  CUSTOM_RANGE: "customRange",
};

export const DATE_RANGE_PRESET_OPTIONS = [
  { id: DATE_PRESET.LAST_1, label: "Last 1 day" },
  { id: DATE_PRESET.LAST_2, label: "Last 2 days" },
  { id: DATE_PRESET.LAST_5, label: "Last 5 days" },
  { id: DATE_PRESET.THIS_WEEK, label: "This week" },
  { id: DATE_PRESET.LAST_7, label: "Last 7 days" },
  { id: DATE_PRESET.LAST_30, label: "Last 30 days" },
  { id: DATE_PRESET.START_TO_NOW, label: "From date" },
];

function companyMoment(timezoneOptions = {}) {
  const offsetMin = parseUtcOffsetMinutes(timezoneOptions.compUTCTimeZone);
  if (offsetMin != null) {
    return moment.utc(Date.now() + offsetMin * 60 * 1000);
  }
  const p = getCompanyNowParts(timezoneOptions);
  return moment(`${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`, "YYYY-MM-DDTHH:mm");
}

/** Rolling ranges ending at company "now". */
export function getDateRangeForPreset(presetId, timezoneOptions = {}) {
  const now = companyMoment(timezoneOptions);
  const endNow = getCompanyNowDate();
  switch (presetId) {
    case DATE_PRESET.LAST_1:
      return [now.clone().startOf("day").toDate(), endNow];
    case DATE_PRESET.LAST_2:
      return [now.clone().subtract(1, "day").startOf("day").toDate(), endNow];
    case DATE_PRESET.LAST_5:
      return [now.clone().subtract(4, "day").startOf("day").toDate(), endNow];
    case DATE_PRESET.THIS_WEEK:
      return [now.clone().startOf("isoWeek").startOf("day").toDate(), endNow];
    case DATE_PRESET.LAST_7:
      return [now.clone().subtract(6, "day").startOf("day").toDate(), endNow];
    case DATE_PRESET.LAST_30:
      return [now.clone().subtract(29, "day").startOf("day").toDate(), endNow];
    default:
      return null;
  }
}

export function toJsDate(v) {
  if (v == null) return null;
  const d = typeof v === "string" ? new Date(v) : v;
  return d instanceof Date && !isNaN(d.getTime()) ? d : null;
}

/**
 * Effective bounds for API. End is always company current date/time (now).
 */
export function getEffectiveReportDateBounds(preset, rawStart, rawEnd, timezoneOptions = {}) {
  const p = preset === "custom" ? DATE_PRESET.LAST_1 : preset || DATE_PRESET.LAST_1;
  const endNow = getCompanyNowDate();

  if (p === DATE_PRESET.START_TO_NOW) {
    const start =
      rawStart && moment(rawStart).isValid()
        ? moment(rawStart).startOf("day").toDate()
        : companyMoment(timezoneOptions).subtract(29, "day").startOf("day").toDate();
    return { startDate: start, endDate: endNow };
  }

  const rolling = getDateRangeForPreset(p, timezoneOptions);
  if (rolling) {
    return { startDate: rolling[0], endDate: rolling[1] };
  }

  if (p === DATE_PRESET.CUSTOM_RANGE) {
    const startDate =
      rawStart && moment(rawStart).isValid()
        ? toJsDate(rawStart)
        : companyMoment(timezoneOptions).startOf("day").toDate();
    return { startDate, endDate: endNow };
  }

  return { startDate: null, endDate: null };
}
